import type { FileAccessOption } from "../api/fileTypes";

const fileExtensionRegExp = /(?:(\.[^.]+))?$/;

const imageFileExtensions = [".png", ".jpg", ".jpeg", ".gif", ".webp"];
const videoFileExtensions = [".mp4", ".webm", ".ogv", ".mov"];

export const getFileExtension = (filename: string): string =>
  filename.toLowerCase().match(fileExtensionRegExp)?.[1] || "";

export const isPdfFile = (filename: string): boolean => getFileExtension(filename) === ".pdf";

export const isImageFile = (filename: string): boolean => imageFileExtensions.includes(getFileExtension(filename));

export const isVideoFile = (filename: string): boolean => videoFileExtensions.includes(getFileExtension(filename));

export const formatFileSize = (fileSize: number): string => {
  if (Number.isNaN(fileSize) || !Number.isFinite(fileSize)) {
    return "";
  }

  if (fileSize <= 0) {
    return "0 bytes";
  }

  const units = ["bytes", "kB", "MB", "GB", "TB"];
  const unitIndex = Math.min(Math.floor(Math.log(fileSize) / Math.log(1000)), units.length - 1);
  const sizeInUnit =
    unitIndex < 3
      ? Math.round(fileSize / Math.pow(1000, unitIndex))
      : Math.round((fileSize / Math.pow(1000, unitIndex)) * 10) / 10;

  return `${sizeInUnit} ${units[unitIndex]}`;
};

export const splitFileIntoChunks = (file: File, chunkSize: number): Blob[] => {
  const chunks: Blob[] = [];
  let offset = 0;

  while (offset < file.size) {
    const chunk = file.slice(offset, offset + chunkSize);
    chunks.push(chunk);
    offset += chunkSize;
  }

  return chunks;
};

export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (event.target?.result) {
        resolve(event.target.result as string);
      } else {
        reject(new Error("Failed to read file content"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsText(file);
  });
};

export const readFileAsDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (event.target?.result) {
        resolve(event.target.result as string);
      } else {
        reject(new Error("Failed to read file content"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(blob);
  });
};

export const isFileDownloadAllowed = (accessOptions?: FileAccessOption[]): boolean =>
  accessOptions === undefined || accessOptions.includes("Download");
